import React, { useState } from 'react'
import type { TOCItem } from '../../lib/reader/toc'
import { useReaderSettings } from './ReaderSettings'

type Props = {
  open: boolean
  onClose: () => void
  toc: TOCItem[]
  activeId?: string | null
  onJumpTo: (id: string) => void
}

export default function TOCSettingsDrawer({ open, onClose, toc, activeId, onJumpTo }: Props) {
  const [tab, setTab] = useState<'contents' | 'settings'>('contents')
  const { settings, setSettings, reset } = useReaderSettings()

  return (
    <div aria-hidden={!open}>
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed right-0 top-0 h-full w-[360px] md:w-[420px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="sticky top-0 bg-neutral-900 border-b border-white/10">
          <div className="flex items-center gap-2 px-3 py-2">
            <button
              className={`px-2.5 py-1 rounded-full text-xs border ${tab === 'contents' ? 'bg-white/10 border-white/20' : 'bg-transparent border-white/10 text-white/80'}`}
              onClick={() => setTab('contents')}
            >
              Contents
            </button>
            <button
              className={`px-2.5 py-1 rounded-full text-xs border ${tab === 'settings' ? 'bg-white/10 border-white/20' : 'bg-transparent border-white/10 text-white/80'}`}
              onClick={() => setTab('settings')}
            >
              Settings
            </button>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">✕</button>
          </div>
        </div>

        {tab === 'contents' ? (
          <div className="chat-scroll overflow-y-auto h-[calc(100%-44px)] px-2 py-2">
            {toc.length === 0 ? (
              <div className="p-4 text-white/80">No headings found</div>
            ) : (
              <ul className="space-y-1">
                {toc.map((t) => (
                  <li key={t.id}>
                    <button
                      onClick={() => onJumpTo(t.id)}
                      className={`block w-full text-left px-2 py-1 rounded-md hover:bg-white/5 ${
                        activeId === t.id ? 'bg-white/10' : ''
                      }`}
                      style={{ paddingLeft: t.level === 1 ? 8 : t.level === 2 ? 20 : 32 }}
                    >
                      <span className="text-sm text-white/90">{t.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="chat-scroll overflow-y-auto h-[calc(100%-44px)] p-3 space-y-4">
            <div>
              <div className="text-sm font-semibold mb-1">Theme</div>
              <div className="flex items-center gap-2">
                {(['light', 'sepia', 'dark'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setSettings({ theme: t })}
                    className={`px-3 py-1 rounded-full border text-xs ${settings.theme === t ? 'bg-white/10 border-white/20' : 'bg-transparent border-white/10 text-white/80'}`}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-1">Font</div>
              <div className="flex items-center gap-2">
                {(['serif', 'sans'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setSettings({ fontFamily: f })}
                    className={`px-3 py-1 rounded-full border text-xs ${settings.fontFamily === f ? 'bg-white/10 border-white/20' : 'bg-transparent border-white/10 text-white/80'}`}
                  >
                    {f === 'serif' ? 'Serif' : 'Sans'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-1">Font Size</div>
              <input
                type="range"
                min={14}
                max={24}
                step={1}
                value={settings.fontSize}
                onChange={(e) => setSettings({ fontSize: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <div className="text-sm font-semibold mb-1">Line Height</div>
              <input
                type="range"
                min={20}
                max={36}
                step={1}
                value={settings.lineHeight}
                onChange={(e) => setSettings({ lineHeight: Number(e.target.value) })}
                className="w-full"
              />
            </div>
            <div>
              <button onClick={reset} className="text-sm text-white/80 underline">Reset to defaults</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

